import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, X, ShoppingBag, Search } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetClose } from "./ui/sheet";
import { NAV, CART } from "../constants/testIds";
import { useCart } from "../context/CartContext";

const links = [
    { id: NAV.linkProduits, label: "Produits", to: "/produits" },
    { id: NAV.linkOffres, label: "Offres", to: "/offres" },
    { id: NAV.linkFaq, label: "FAQ", to: "/#faq" },
];

const Navbar = ({ onQuoteClick }) => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const { totalQty, setOpen: setCartOpen } = useCart();
    const navigate = useNavigate();

    // La loupe emmène vers /produits et demande le focus du champ de recherche
    // (state de navigation : pas de query param, aucun impact SEO/historique).
    const focusCatalogSearch = () => {
        navigate("/produits", { state: { focusSearch: true } });
    };

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 12);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <header
            data-testid={NAV.root}
            className={`sticky top-0 z-50 transition-all duration-300 ${
                scrolled
                    ? "dp-glass border-b border-black/5"
                    : "bg-transparent border-b border-transparent"
            }`}
        >
            <div className="max-w-[1320px] mx-auto px-6 md:px-10 h-[68px] flex items-center justify-between">
                {/* Logo */}
                <Link
                    to="/"
                    data-testid={NAV.logo}
                    className="flex items-center gap-2.5 group"
                >
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-[8px] bg-[#1C1C1E]">
                        <span className="bg-gradient-to-br from-[#d4af37] to-[#8b5cf6] bg-clip-text font-display text-[13px] font-bold leading-none text-transparent select-none">
                            AX
                        </span>
                    </span>
                    <span className="font-display text-[17px] font-semibold tracking-tight">
                        Atelier AX
                    </span>
                </Link>

                {/* Center links */}
                <nav className="hidden lg:flex items-center gap-1">
                    {links.map((l) =>
                        l.to ? (
                            <Link
                                key={l.id}
                                to={l.to}
                                data-testid={l.id}
                                className="px-4 py-2 text-[14px] text-neutral-700 hover:text-black rounded-full hover:bg-black/[0.04] transition-colors"
                            >
                                {l.label}
                            </Link>
                        ) : (
                            <a
                                key={l.id}
                                data-testid={l.id}
                                href={l.href}
                                className="px-4 py-2 text-[14px] text-neutral-700 hover:text-black rounded-full hover:bg-black/[0.04] transition-colors"
                            >
                                {l.label}
                            </a>
                        )
                    )}
                </nav>

                {/* CTA */}
                <div className="hidden lg:flex items-center gap-2">
                    {/* Search button */}
                    <button
                        type="button"
                        onClick={focusCatalogSearch}
                        aria-label="Rechercher un produit"
                        className="relative w-10 h-10 rounded-full border border-black/10 hover:border-black/30 flex items-center justify-center transition-colors"
                    >
                        <Search className="w-4 h-4" />
                    </button>

                    {/* Cart button */}
                    <button
                        type="button"
                        onClick={() => setCartOpen(true)}
                        data-testid={CART.button}
                        aria-label="Voir le panier"
                        className="relative w-10 h-10 rounded-full border border-black/10 hover:border-black/30 flex items-center justify-center transition-colors"
                    >
                        <ShoppingBag className="w-4 h-4" />
                        {totalQty > 0 && (
                            <span
                                data-testid={CART.badge}
                                className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#d4af37] text-white text-[10px] font-mono-tech flex items-center justify-center tabular-nums leading-none"
                            >
                                {totalQty}
                            </span>
                        )}
                    </button>
                </div>

                {/* Mobile actions */}
                <div className="lg:hidden flex items-center gap-2">
                    <button
                        type="button"
                        onClick={focusCatalogSearch}
                        aria-label="Rechercher un produit"
                        className="relative w-10 h-10 rounded-full border border-black/10 flex items-center justify-center"
                    >
                        <Search className="w-4 h-4" />
                    </button>
                    <button
                        type="button"
                        onClick={() => setCartOpen(true)}
                        data-testid={`${CART.button}-mobile`}
                        aria-label="Voir le panier"
                        className="relative w-10 h-10 rounded-full border border-black/10 flex items-center justify-center"
                    >
                        <ShoppingBag className="w-4 h-4" />
                        {totalQty > 0 && (
                            <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#d4af37] text-white text-[10px] flex items-center justify-center tabular-nums leading-none">
                                {totalQty}
                            </span>
                        )}
                    </button>
                    <Sheet open={open} onOpenChange={setOpen}>
                        <SheetTrigger asChild>
                            <button
                                type="button"
                                aria-label="Menu"
                                className="inline-flex items-center justify-center w-10 h-10 rounded-full border border-black/10"
                            >
                                {open ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
                            </button>
                        </SheetTrigger>
                        <SheetContent
                            side="top"
                            className="lg:hidden top-0 h-auto bg-white/95 backdrop-blur-xl border-b border-black/5 [&>button]:hidden"
                        >
                            {/* Le menu couvre tout l'écran depuis le haut (bandeau
                                + navbar compris) : il a donc son propre bouton
                                de fermeture. */}
                            <div className="flex items-center justify-between pb-2">
                                <span className="font-display text-[15px] font-semibold">Menu</span>
                                <SheetClose asChild>
                                    <button
                                        type="button"
                                        aria-label="Fermer le menu"
                                        className="inline-flex items-center justify-center w-10 h-10 rounded-full border border-black/10"
                                    >
                                        <X className="w-4 h-4" />
                                    </button>
                                </SheetClose>
                            </div>
                            <nav className="flex flex-col gap-1">
                                {links.map((l) =>
                                    l.to ? (
                                        <SheetClose asChild key={l.id}>
                                            <Link
                                                to={l.to}
                                                className="py-3 text-[15px] text-neutral-800"
                                            >
                                                {l.label}
                                            </Link>
                                        </SheetClose>
                                    ) : (
                                        <SheetClose asChild key={l.id}>
                                            <a
                                                href={l.href}
                                                className="py-3 text-[15px] text-neutral-800"
                                            >
                                                {l.label}
                                            </a>
                                        </SheetClose>
                                    )
                                )}
                            </nav>
                        </SheetContent>
                    </Sheet>
                </div>
            </div>
        </header>
    );
};

export default Navbar;
